import React from "react";
import {
  BODY_BASE, FRONT_ZONES, BACK_ZONES, ZONE_CENTER, ZONE_LABELS, LEVEL_COLOR, heatFill,
} from "./bodyMapConfig";

const NEUTRAL_STROKE = "rgba(255,255,255,0.14)";

function Shape({ s, fill, stroke, sw, onClick }) {
  const c = { fill, stroke, strokeWidth: sw, onClick,
    style: { cursor: onClick ? "pointer" : "default", transition: "fill 0.25s ease, stroke 0.2s ease" } };
  if (s.t === "c") return <circle cx={s.cx} cy={s.cy} r={s.r} {...c} />;
  if (s.t === "e") return <ellipse cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} {...c} />;
  if (s.t === "r") return <rect x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx} {...c} />;
  return <path d={s.d} {...c} />;
}

const centers = (shapes, part) => {
  const out = [];
  shapes.forEach((s) => {
    if (s.t === "e" || s.t === "c") out.push([s.cx, s.cy]);
    else if (s.t === "r") out.push([s.x + s.w / 2, s.y + s.h / 2]);
  });
  if (!out.length && ZONE_CENTER[part]) out.push(ZONE_CENTER[part]);
  return out;
};

// mode "input": values={part:level}, onSelect(part), selectedPart. mode "heat": heat={part:{...}}.
export default function BodyMap({
  mode = "input", values = {}, heat = {}, onSelect, selectedPart,
  accent = "#C41E24", view = "front", onViewChange, showToggle = true,
}) {
  const isHeat = mode === "heat";
  const zones = view === "front" ? FRONT_ZONES : BACK_ZONES;

  return (
    <div className="relative w-full max-w-[260px] mx-auto select-none">
      {/* Front / Back toggle */}
      {showToggle && <div className="flex justify-center mb-3">
        <div className="inline-flex items-center gap-1 bg-black/40 border border-white/10 rounded-full p-1">
          {["front", "back"].map((v) => (
            <button key={v} type="button" data-testid={`bodymap-view-${v}`}
              onClick={() => onViewChange && onViewChange(v)}
              className="px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all"
              style={{ background: view === v ? accent : "transparent", color: view === v ? "#fff" : "rgba(255,255,255,0.5)" }}>
              {v}
            </button>
          ))}
        </div>
      </div>}

      <svg viewBox="0 0 220 470" className="w-full h-auto" role="img" aria-label={`Body strain map, ${view} view`}>
        <defs>
          <radialGradient id="bmBody" cx="50%" cy="28%" r="75%">
            <stop offset="0%" stopColor="#33383f" />
            <stop offset="100%" stopColor="#1c1f24" />
          </radialGradient>
          <radialGradient id="bmGlow" cx="50%" cy="26%" r="70%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.14" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
          <filter id="bmSoft" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.2" />
          </filter>
        </defs>
        <rect x="0" y="0" width="220" height="470" fill="url(#bmGlow)" />

        {/* Neutral body silhouette */}
        <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
          {BODY_BASE.map((s, i) => <Shape key={i} s={s} fill="url(#bmBody)" stroke="rgba(255,255,255,0.06)" sw={1} />)}
        </g>

        {/* Interactive muscle groups */}
        {Object.entries(zones).map(([part, shapes]) => {
          const lvl = values[part];
          let fill = "rgba(255,255,255,0.05)";
          if (isHeat) { fill = heatFill(heat[part]) || "rgba(255,255,255,0.05)"; }
          else if (lvl) { fill = LEVEL_COLOR[lvl]; }
          const isSel = !isHeat && selectedPart === part;
          const stroke = isSel ? accent : (fill.startsWith("rgba") ? NEUTRAL_STROKE : "rgba(0,0,0,0.35)");
          const glowing = (!isHeat && lvl && lvl !== "green") || (isHeat && heatFill(heat[part]));
          return (
            <g key={part} data-testid={`bodymap-zone-${part}`}
               onClick={onSelect ? () => onSelect(part) : undefined}
               style={{ cursor: onSelect ? "pointer" : "default" }}>
              {glowing && shapes.map((s, i) => <Shape key={`g${i}`} s={{ ...s }} fill={fill} stroke="none" sw={0} />)}
              {glowing && <g filter="url(#bmSoft)" opacity="0.5">{shapes.map((s, i) => <Shape key={`b${i}`} s={s} fill={fill} stroke="none" sw={0} />)}</g>}
              {shapes.map((s, i) => (
                <Shape key={i} s={s} fill={glowing ? fill : (isSel ? `${accent}22` : fill)} stroke={stroke} sw={isSel ? 3 : 1.5} onClick={onSelect ? () => onSelect(part) : undefined} />
              ))}
            </g>
          );
        })}

        {/* Heat count badges */}
        {isHeat && Object.entries(zones).map(([part, shapes]) => {
          const c = heat[part];
          const total = c ? (c.yellow + c.orange + c.red) : 0;
          if (!total) return null;
          return centers(shapes, part).map(([x, y], i) => (
            <g key={`bd-${part}-${i}`} pointerEvents="none">
              <circle cx={x} cy={y} r="10" fill="rgba(0,0,0,0.72)" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              <text x={x} y={y + 3.5} textAnchor="middle" fontSize="10" fontWeight="800" fill="#fff">{total}</text>
            </g>
          ));
        })}

        {/* Selected label pin (input) */}
        {!isHeat && selectedPart && zones[selectedPart] && centers(zones[selectedPart], selectedPart).slice(0, 1).map(([x, y], i) => (
          <g key={`sel-${i}`} pointerEvents="none">
            <circle cx={x} cy={y} r="4" fill={accent} stroke="#fff" strokeWidth="1.5" />
          </g>
        ))}
      </svg>
    </div>
  );
}
